import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: pure modules, resolvable under Jest without a shim.
import { allAnswered } from "../../pathways/learnerModel";
import { pathwayErrorMessage } from "../../pathways/errors";
import { PATHWAYS_LEARNER as IDS } from "../../constants/testIds/pathwaysLearner";

const unanswered = (questions) => questions.map(() => -1);

/**
 * A quiz step (contract §5.4): the questions, one radio group each; "Submit answers";
 * then the score, passed or not against the pass mark, and "Try again" after a fail.
 * Retries are unlimited until passed, and a pass completes the step on the server.
 *
 * WHAT IT SENDS: `onSubmitQuiz(answers)`, one choice index per question, in order. Every
 * question must be answered first: an omission is pointed at, not spent as an attempt.
 *
 * WHAT IT NEVER KNOWS: the answer key. After a submission each question is marked from the
 * grading `detail` (`is_correct` of THE LEARNER'S answer), never with the right choice.
 *
 * A quiz already passed (`quiz.passed` on the screen) shows its result and no form.
 *
 * @param {object} props
 * @param {{ pass_threshold: number, questions: {id: string, question: string, choices: string[]}[],
 *           attempts: number, best_score: number|null, passed: boolean }} props.quiz
 * @param {(answers: number[]) => Promise<object>} props.onSubmitQuiz  resolves a QuizSubmitResult
 * @param {boolean} props.disabled   the practitioner's preview: nothing is submitted
 */
export default function StepQuiz({ quiz, onSubmitQuiz, disabled }) {
  const t = useT("public");
  const tc = useT("common");
  const [answers, setAnswers] = useState(() => unanswered(quiz.questions));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const attempts = result ? result.attempts : quiz.attempts;
  const best = result ? result.best_score : quiz.best_score;
  const passedBefore = quiz.passed && !result;
  const marks = {};
  (result?.detail || []).forEach((d) => { marks[d.question_id] = d; });
  const reviewing = Boolean(result);

  const choose = (qIndex, cIndex) => {
    setError("");
    setAnswers((prev) => prev.map((a, i) => (i === qIndex ? cIndex : a)));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy || disabled || reviewing) return;
    if (!allAnswered(answers)) { setError(t("pathways.quiz.answerAll")); return; }
    setBusy(true);
    setError("");
    try {
      setResult(await onSubmitQuiz(answers));
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, t("pathways.quiz.submitFailed")));
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setResult(null);
    setError("");
    setAnswers(unanswered(quiz.questions));
  };

  const attemptsLine = attempts > 0 && (
    <p data-testid={IDS.quizAttempts} className="text-xs text-eh-muted">
      {best === null || best === undefined
        ? t("pathways.quiz.attempts", { n: attempts })
        : t("pathways.quiz.attemptsBest", { n: attempts, best })}
    </p>
  );

  if (passedBefore) {
    return (
      <div data-testid={IDS.quiz} className="space-y-2">
        <p data-testid={IDS.quizResult} data-passed="true" className="rounded-2xl bg-eh-sage-soft px-4 py-3 text-sm font-medium text-eh-ink">
          {t("pathways.quiz.alreadyPassed", { score: best ?? 0 })}
        </p>
        {attemptsLine}
      </div>
    );
  }

  return (
    <form data-testid={IDS.quiz} onSubmit={submit} noValidate className="space-y-5">
      <p className="text-sm text-eh-muted">{t("pathways.quiz.threshold", { n: quiz.pass_threshold })}</p>
      {quiz.questions.map((q, qIndex) => {
        const mark = marks[q.id];
        return (
          <fieldset key={q.id} className="space-y-2">
            <legend className="text-sm font-medium text-eh-ink break-words">
              {qIndex + 1}. {q.question}
            </legend>
            {q.choices.map((choice, cIndex) => (
              <label key={`${q.id}-${cIndex}`} className="flex items-start gap-3 rounded-xl border border-eh-border-soft bg-eh-surface px-3 py-2.5 text-sm text-eh-ink">
                <input
                  type="radio"
                  name={`pathway-quiz-${q.id}`}
                  value={cIndex}
                  checked={answers[qIndex] === cIndex}
                  onChange={() => choose(qIndex, cIndex)}
                  disabled={reviewing || busy}
                  data-testid={IDS.quizChoice(q.id, cIndex)}
                  className="mt-0.5 accent-eh-ink"
                />
                <span className="min-w-0 break-words">{choice}</span>
              </label>
            ))}
            {mark && (
              <p
                data-testid={IDS.quizQuestionResult(q.id)}
                data-correct={mark.is_correct ? "true" : "false"}
                className={`text-xs font-medium ${mark.is_correct ? "text-eh-sage-deep" : "text-rose-700"}`}
              >
                {mark.is_correct ? t("pathways.quiz.correct") : t("pathways.quiz.incorrect")}
              </p>
            )}
          </fieldset>
        );
      })}

      {error && <p role="alert" data-testid={IDS.quizError} className="text-sm text-rose-700">{error}</p>}

      {result && (
        <div
          role="status"
          data-testid={IDS.quizResult}
          data-passed={result.passed ? "true" : "false"}
          className={`rounded-2xl px-4 py-3 text-sm font-medium text-eh-ink ${result.passed ? "bg-eh-sage-soft" : "bg-eh-ochre/10"}`}
        >
          {result.passed
            ? t("pathways.quiz.passed", { score: result.score })
            : t("pathways.quiz.failed", { score: result.score, threshold: result.threshold })}
        </div>
      )}

      {attemptsLine}

      {result && !result.passed ? (
        <button
          type="button"
          onClick={retry}
          data-testid={IDS.quizRetry}
          className="inline-flex h-11 items-center justify-center rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90"
        >
          {t("pathways.quiz.retry")}
        </button>
      ) : !result && (
        <button
          type="submit"
          disabled={busy || disabled}
          data-testid={IDS.quizSubmit}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-eh-ink px-6 text-sm font-semibold text-eh-cream hover:bg-eh-ink/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
          {t("pathways.quiz.submit")}
        </button>
      )}
    </form>
  );
}
